import { flatten } from 'utils/flatten/flatten.util';
import messagesInDutch from 'lang/nl-NL.json';
import messagesInEnglish from 'lang/en.json';
import messagesInFrenchCanada from 'lang/fr-CA.json';
import messagesInFrenchStandard from 'lang/fr-FR.json';
import messagesInGerman from 'lang/de-DE.json';
import messagesInHungarian from 'lang/hu-HU.json';
import messagesInSpanish from 'lang/es-ES.json';
import messagesInItalian from 'lang/it-IT.json';
import messagesInNorwegian from 'lang/nb-NO.json';
import messagesInPolish from 'lang/pl.json';
import messagesInPortuguese from 'lang/pt-PT.json';
import messagesInSlovak from 'lang/sk-SK.json';
import messagesInUnitedKingdomEnglish from 'lang/en-GB.json';
import messagesInUnitedStatesEnglish from 'lang/en-US.json';
import messagesInAustralianEnglish from 'lang/en-AU.json';
import messagesInCanadianEnglish from 'lang/en-CA.json';
import messagesInIrishEnglish from 'lang/en-IE.json';
import messagesInIndianEnglish from 'lang/en-IN.json';
import messagesInNewZealandEnglish from 'lang/en-NZ.json';
import { useMemo } from 'react';

export const DEFAULT_LOCALE = 'en';

const messagesMap = new Map<string, Record<string, unknown>>([
  ['de-de', messagesInGerman.resourceBooking],
  ['en', messagesInEnglish.resourceBooking],
  ['es-es', messagesInSpanish.resourceBooking],
  ['fr-fr', messagesInFrenchStandard.resourceBooking],
  ['fr-ca', messagesInFrenchCanada.resourceBooking],
  ['nl-nl', messagesInDutch.resourceBooking],
  ['it-it', messagesInItalian.resourceBooking],
  ['nb-no', messagesInNorwegian.resourceBooking],
  ['pl', messagesInPolish.resourceBooking],
  ['en-gb', messagesInUnitedKingdomEnglish.resourceBooking],
  ['en-us', messagesInUnitedStatesEnglish.resourceBooking],
  ['en-au', messagesInAustralianEnglish.resourceBooking],
  ['en-ca', messagesInCanadianEnglish.resourceBooking],
  ['en-ie', messagesInIrishEnglish.resourceBooking],
  ['en-in', messagesInIndianEnglish.resourceBooking],
  ['en-nz', messagesInNewZealandEnglish.resourceBooking],
  ['pt-pt', messagesInPortuguese.resourceBooking],
  ['pt', messagesInPortuguese.resourceBooking],
  ['sk-sk', messagesInSlovak.resourceBooking],
  ['sk', messagesInSlovak.resourceBooking],
  ['hu-hu', messagesInHungarian.resourceBooking],
  ['hu', messagesInHungarian.resourceBooking],
]);

const defaultMessages = flatten(messagesMap.get(DEFAULT_LOCALE)) as Record<string, string>;

const getAvailableLocale = (userLocale: string): [string, string] => {
  // Normalize to lowercase so conventional BCP 47 inputs (e.g. `en-US`, `fr-FR`)
  // match the lowercase catalog keys instead of silently falling back to English.
  const normalizedLocale = userLocale?.toLowerCase() ?? '';

  if (messagesMap.has(normalizedLocale)) {
    return [normalizedLocale, userLocale];
  }

  const [lang] = normalizedLocale.split('-');

  if (messagesMap.has(lang)) {
    return [lang, userLocale];
  }

  return [DEFAULT_LOCALE, DEFAULT_LOCALE];
};

interface LocaleData {
  messages: Record<string, string>;
  locale: string;
}

export const useLocaleData = (userLocale: string): LocaleData => {
  return useMemo(() => {
    const [messagesKey, locale] = getAvailableLocale(userLocale);

    return {
      locale,
      // Spread over the English messages so untranslated keys fall back to English instead of rendering raw ids.
      messages: { ...defaultMessages, ...(flatten(messagesMap.get(messagesKey)) as Record<string, string>) },
    };
  }, [userLocale]);
};
